import { BarDropdown } from '../bar-dropdown/BarDropdown'
import * as React from 'react'
import { Card, Col, List, Row, Tooltip } from 'antd'
import { NotificationsIcon } from '../../icons/NotificationsIcon'
import styles from './styles.module.scss'
import { BellFilled } from '@ant-design/icons'
import {
  DownTimeIcon,
  EnhancementsIcon,
  InsightIcon,
  KnownIssuesIcon,
  GeneralNotificationsIcon,
  DataReleaseIcon
} from './icons'
import { useEffect, useState } from 'react'
import {
  determineNotificationAge,
  determineUnreadCount,
  Label,
  Notification,
  sortNotifications
} from "./notification-utils";

export type NotificationProps = {
  notifications: Notification[] | undefined
  markNotificationsRead: () => Promise<void>
}

export type NotificationPanelProps = {
  notifications: Notification[] | undefined
}

interface LabelIconProps {
  className?: string
}

const Labels: {
  [key in keyof typeof Label]: { icon: React.FC<LabelIconProps>; text: string }
} = {
  DOWN_TIME: {
    text: 'Downtime',
    icon: ({ className }: LabelIconProps) => (
      <DownTimeIcon className={className} />
    )
  },
  ENHANCEMENTS: {
    text: 'Tool Enhancements',
    icon: ({ className }: LabelIconProps) => (
      <EnhancementsIcon className={className} />
    )
  },
  INSIGHT: {
    text: 'Insight',
    icon: ({ className }: LabelIconProps) => (
      <InsightIcon className={className} />
    )
  },
  KNOWN_ISSUES: {
    text: 'Known Issues',
    icon: ({ className }: LabelIconProps) => (
      <KnownIssuesIcon className={className} />
    )
  },
  MISC: {
    text: 'General Notifications',
    icon: ({ className }: LabelIconProps) => (
      <GeneralNotificationsIcon className={className} />
    )
  },
  DATA_RELEASE: {
    text: 'Data release',
    icon: ({ className }: LabelIconProps) => (
      <DataReleaseIcon className={className} />
    )
  }
}

const Icon = ({ unreadCount }: { unreadCount: number }) => {
  return (
    <Tooltip
      align={{ offset: [-7] }}
      overlayClassName='nav-bar-app'
      placement='bottom'
      title='Notifications'
    >
      <NotificationsIcon
        className={styles.notificationsIcon}
        count={unreadCount}
      />
    </Tooltip>
  )
}

export const Panel = ({ notifications }: NotificationPanelProps) => {
  return (
    <List
      className={styles.notificationsList}
      header='Notifications'
      dataSource={sortNotifications(notifications)}
      renderItem={(notification) => (
        <Card className={styles.notificationContainer} key={notification.id}>
          <Row>
            <Col className={styles.notificationLabelIconWrapper} span={4}>
              {Labels[notification.label].icon({
                className: styles.notificationLabelIcon
              })}
            </Col>
            <Col span={13}>
              <div className={styles.notificationTitle}>
                {Labels[notification.label].text.toUpperCase()}
              </div>
              <div className={styles.notificationMessage}>
                {notification.message}
              </div>
            </Col>
            <Col span={5}>
              <div className={styles.notificationAge}>
                {determineNotificationAge(new Date(), notification.startDate)}
              </div>
            </Col>
            <Col span={1}>
              <div className={styles.notificationReadStatus}>
                {notification.isReadForUser ? (
                  <div />
                ) : (
                  <div>
                    <BellFilled className={styles.notificationBell} />
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Card>
      )}
    />
  )
}

export const Notifications = ({
  notifications,
  markNotificationsRead
}: NotificationProps) => {
  const [unreadCount, setUnreadCount] = useState(
    determineUnreadCount(notifications)
  )

  useEffect(() => {
    setUnreadCount(determineUnreadCount(notifications))
  }, [notifications])

  return (
    <BarDropdown
      dropDownClassName={styles.dropDown}
      onVisibleChange={(isVisible) => {
        if (unreadCount > 0 && isVisible) {
          setUnreadCount(0)
        }
        if (!isVisible) {
          markNotificationsRead().then(() => setUnreadCount(0))
        }
      }}
      icon={<Icon unreadCount={unreadCount} />}
      dataTestId={'notifications-dropdown'}
    >
      <Panel notifications={notifications} />
    </BarDropdown>
  )
}
